import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Dashboard as DashboardIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { formatDate, formatTotals } from '../../services/expenseVouchersApi';
import { formatMoney } from '../../services/expenseAccountsApi';
import { operationsApi, WALLET_POSITION_LABELS } from '../../services/expenseOperationsApi';
import { PaymentStatusChip, useExpenseBase, VoucherStatusChip } from '../../components/expense/voucherShared';

// [key, label, list query, color]: each card opens My Expenses filtered to the same vouchers
const STATUS_CARDS = [
  ['draft', 'Draft', 'status=DRAFT', 'text.primary'],
  ['sentBack', 'Sent Back', 'status=SENT_BACK', 'warning.main'],
  ['pendingAccounts', 'Pending Accounts', 'status=PENDING_ACCOUNTS', 'info.main'],
  ['pendingReporting', 'Pending Reporting approval', 'status=PENDING_APPROVAL', 'secondary.main'],
  ['pendingManagement', 'Pending Management approval', 'status=PENDING_APPROVAL', 'secondary.main'],
  ['approved', 'Approved (awaiting payment)', 'paymentStatus=UNPROCESSED,IN_PROCESS,PARTIALLY_PAID', 'success.main'],
  ['paid', 'Paid / Settled', 'paymentStatus=PAID', 'success.main'],
  ['onHold', 'On Hold', 'paymentStatus=ON_HOLD', 'secondary.main'],
  ['cancelled', 'Cancelled', 'paymentStatus=CANCELLED', 'error.main'],
  ['rejected', 'Rejected', 'status=REJECTED', 'error.main'],
];

const CountCard = ({ label, value, color, to, caption }) => (
  <Card variant="outlined" sx={{ height: '100%' }} data-testid={`count-${label}`}>
    <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
        {caption && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{caption}</Typography>}
      </CardContent>
    </CardActionArea>
  </Card>
);

const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };

// Employee Expense dashboard: the employee's own vouchers, wallet and advances only (the
// server scopes every figure to the logged-in employee). Money is shown per currency and
// never added across currencies.
const MyExpenseDashboard = () => {
  const base = useExpenseBase();
  const prefix = base.startsWith('/admin') ? '/admin' : '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await operationsApi.myDashboard()).data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const c = data?.counts || {};
  const paidCaption = data && c.paid
    ? [
      c.paidCash ? `${c.paidCash} paid` : null,
      c.settledWallet ? `${c.settledWallet} settled by wallet` : null,
      c.completedNoPayment ? `${c.completedNoPayment} nothing payable` : null,
    ].filter(Boolean).join(' · ')
    : null;

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <DashboardIcon color="primary" />
          My Expense Dashboard
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        <Button variant="contained" component={RouterLink} to={`${base}/new`}>New Expense</Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your own expense vouchers, wallet and advances. Click a figure to open the matching vouchers.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {loading && !data && <CircularProgress />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {STATUS_CARDS.map(([key, label, query, color]) => (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={key}>
                <CountCard
                  label={label}
                  value={c[key] ?? 0}
                  color={color}
                  to={`${base}?${query}`}
                  caption={key === 'paid' ? paidCaption : null}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" sx={{ mb: 1 }}>Wallet position</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Each currency is kept separately and never combined.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }} data-testid="dashboard-wallet">
            {data.wallet.length === 0 && <Typography color="text.secondary">No wallet balance yet.</Typography>}
            {data.wallet.map((w) => (
              <Card key={w.currency} variant="outlined" sx={{ minWidth: 200, borderLeft: 4, borderColor: w.position === 'PAYABLE' ? 'info.main' : w.position === 'RECOVERABLE' ? 'warning.main' : 'divider' }} data-testid={`wallet-${w.currency}`}>
                <CardActionArea component={RouterLink} to={`${prefix}/expense-wallet`}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{w.currency}</Typography>
                    <Typography variant="caption" color="text.secondary">{WALLET_POSITION_LABELS[w.position]}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatMoney(w.amount, w.currency)}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ mb: 1 }}>Advances</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" data-testid="dashboard-advances">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      {['Currency', 'Unsettled', 'In settlement', 'Settled', 'Advance requested'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.advances.length === 0 && (
                      <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No advances.</Typography></TableCell></TableRow>
                    )}
                    {data.advances.map((t) => (
                      <TableRow key={t.currency}>
                        <TableCell>{t.currency}</TableCell>
                        <TableCell>{t.unsettled}</TableCell>
                        <TableCell>{t.inSettlement}</TableCell>
                        <TableCell>{t.settled}</TableCell>
                        <TableCell>{formatMoney(t.advance, t.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="h6" sx={{ mb: 1 }}>Settlements</Typography>
              <Card variant="outlined" data-testid="dashboard-settlements">
                <CardContent>
                  <Typography>Draft: <strong>{data.settlements.draft}</strong></Typography>
                  <Typography>Pending: <strong>{data.settlements.pending}</strong></Typography>
                  <Typography>Approved: <strong>{data.settlements.approved}</strong></Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 1 }}>Recent expenses</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" data-testid="dashboard-recent">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  {['Voucher No.', 'Date', 'Total', 'Status', 'Payment', 'Last Updated'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recent.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography color="text.secondary">You have no expense vouchers yet.</Typography></TableCell></TableRow>
                )}
                {data.recent.map((v) => (
                  <TableRow key={v._id} hover>
                    <TableCell><Link component={RouterLink} to={`${base}/${v._id}`}>{v.voucherNumber}</Link></TableCell>
                    <TableCell>{formatDate(v.voucherDate)}</TableCell>
                    <TableCell>{formatTotals(v)}</TableCell>
                    <TableCell><VoucherStatusChip status={v.status} /></TableCell>
                    <TableCell><PaymentStatusChip status={v.paymentStatus} settledVia={v.settledVia} /></TableCell>
                    <TableCell>{formatDate(v.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default MyExpenseDashboard;
